import { NgModule } from '@angular/core';
import { RouterModule, Routes, PreloadAllModules } from '@angular/router';

import { Shell } from './shell/services/shell/shell.service';
import { AuthenticationGuard } from './@core/authentication/guards/authentication.guard';
import { ClientContextGuard } from './@core/authentication/guards/client-context.guard';
import { LegacyClientUrlGuard } from './@core/authentication/guards/legacy-client-url.guard';
import { NotificationRouteGuard } from './@core/authentication/guards/notification-route.guard';
import { RootRedirectGuard } from './@core/authentication/guards/root-redirect.guard';

import { AssistantAuthorizationComponent } from './interfaces/pages/assistant-authorization/assistant-authorization.component';
import { ClientComponent } from './client/pages/client/client.component';
import { LoginComponent } from './login/pages/login/login.component';
import { LostPasswordComponent } from './login/pages/lost-password/lost-password.component';
import { SubscribeComponent } from './login/pages/subscribe/subscribe.component';
import { VerifyUserComponent } from './login/pages/verify-user/verify-user.component';
import { NotFoundComponent } from './@shared/pages/not-found/not-found.component';
import { ResetPasswordComponent } from './login/pages/reset-password/reset-password.component';
import { VerifyEmailComponent } from './login/pages/verify-email/verify-email.component';

/**
 * Écrans d'un dossier, montés sous `/c/:clientId` (#173).
 *
 * `ClientContextGuard` porté par la route parente garantit que
 * `appService.selectedClient` est chargé **avant** les gardes des routes enfants
 * (`CountryGuard`, `FeatureGuard`), qui le lisent de façon synchrone.
 */
const CLIENT_ROUTES: Routes = [
    { path: 'accounting', loadChildren: () => import('./accounting/accounting.module').then(m => m.AccountingModule) },
    { path: 'assistant', loadChildren: () => import('./ai/ai.module').then(m => m.AiModule) },
    { path: 'banking', loadChildren: () => import('./banking/banking.module').then(m => m.BankingModule) },
    { path: 'client', loadChildren: () => import('./client/client.module').then(m => m.ClientModule) },
    { path: 'dashboard', loadComponent: () => import('./home/pages/dashboard/dashboard.component').then(m => m.DashboardComponent), title: 'Dashboard', data: { screen: 'home.dashboard' } },
    { path: 'documentation', loadChildren: () => import('./documentation/documentation.module').then(m => m.DocumentationModule) },
    { path: 'human-resources', loadChildren: () => import('./human-resources/human-resources.module').then(m => m.HumanResourcesModule) },
    { path: 'interfaces', loadChildren: () => import('./interfaces/interfaces.module').then(m => m.InterfacesModule) },
    { path: 'invoicing', loadChildren: () => import('./invoicing/invoicing.module').then(m => m.InvoicingModule) },
    { path: 'messaging', loadChildren: () => import('./messaging/messaging.module').then(m => m.MessagingModule) },
    { path: 'products', loadChildren: () => import('./products/products.module').then(m => m.ProductsModule) },
    { path: 'purchasing', loadChildren: () => import('./purchasing/purchasing.module').then(m => m.PurchasingModule) },
    { path: 'reports', loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule) },
    { path: 'sales', loadChildren: () => import('./sales/sales.module').then(m => m.SalesModule) },
    { path: 'settings', loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule) },
    { path: 'taxation', loadChildren: () => import('./taxation/taxation.module').then(m => m.TaxationModule) },
    { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
];

/**
 * Préfixes de l'ancienne forme d'URL, sans dossier. Conservés **définitivement**
 * pour les favoris et les liens déjà partagés : ils renvoient au sélecteur en
 * gardant l'écran visé (cf. `LegacyClientUrlGuard`).
 */
const LEGACY_CLIENT_PATHS = [
    'accounting', 'assistant', 'banking', 'client', 'documentation', 'human-resources',
    'interfaces', 'invoicing', 'messaging', 'products', 'purchasing', 'reports',
    'sales', 'settings', 'taxation',
];

const routes: Routes = [
    Shell.childRoutes([
        // Écrans d'un dossier
        { path: 'c/:clientId', canActivate: [ClientContextGuard], children: CLIENT_ROUTES },

        // Écrans hors dossier
        { path: 'about', loadChildren: () => import('./about/about.module').then(m => m.AboutModule) },
        { path: 'announcements', loadChildren: () => import('./announcements/announcements.module').then(m => m.AnnouncementsModule) },
        // Création d'une société : par construction sans dossier, donc hors contexte.
        // `ClientComponent` affiche son formulaire de création quand aucun dossier
        // n'est sélectionné ; `creation` le lui impose même si un dossier l'est.
        { path: 'client/new', component: ClientComponent, data: { creation: true, screen: 'client.creation-societe' }, title: 'Nouvelle société' },
        { path: 'monitoring', loadChildren: () => import('./monitoring/monitoring.module').then(m => m.MonitoringModule) },
        // Point d'entrée des notifications push : le service worker y recopie la charge
        // du push, le garde la traduit en écran. Jamais rendu (#173).
        { path: 'notifications/open', canActivate: [NotificationRouteGuard], component: NotFoundComponent },
        { path: 'user', loadChildren: () => import('./user/user.module').then(m => m.UserModule) },

        // Anciennes URL sans dossier. `home/dashboard` est déclaré avant `home`, qui
        // ne porte plus le tableau de bord. Les composants ne sont jamais rendus :
        // les gardes renvoient toujours une redirection.
        { path: 'home/dashboard', canActivate: [LegacyClientUrlGuard], component: NotFoundComponent },
        { path: 'home', loadChildren: () => import('./home/home.module').then(m => m.HomeModule) },
        ...LEGACY_CLIENT_PATHS.map(path => ({
            path,
            canActivate: [LegacyClientUrlGuard],
            children: [{ path: '**', component: NotFoundComponent }],
        })),

        { path: '', pathMatch: 'full' as const, canActivate: [RootRedirectGuard], component: NotFoundComponent },
    ]),
    { path: 'legal', loadChildren: () => import('./legal/legal.module').then(m => m.LegalModule) },
    { path: 'login', component: LoginComponent, title: 'Se connecter', data: { screen: 'login.connexion' } },
    { path: 'lost-password', component: LostPasswordComponent, title: 'Mot de passe oublié', data: { screen: 'login.mot-de-passe-oublie' } },
    // Page d'autorisation d'un assistant IA (#1312) : ouverte par l'assistant, hors dossier
    // et hors shell, derrière la connexion — le garde conserve l'URL complète dans `redirect`.
    { path: 'oauth/authorize', canActivate: [AuthenticationGuard], component: AssistantAuthorizationComponent, title: 'Autoriser un assistant IA', data: { screen: 'interfaces.autorisation-assistant' } },
    { path: 'reset-password', component: ResetPasswordComponent, title: 'Nouveau mot de passe', data: { screen: 'login.nouveau-mot-de-passe' } },
    { path: 'subscribe', component: SubscribeComponent, title: 'S\'inscrire', data: { screen: 'login.inscription' } },
    { path: 'verify-email', component: VerifyEmailComponent, title: 'Vérification de l\'email', data: { screen: 'login.verification-email' } },
    { path: 'verify-user', component: VerifyUserComponent, title: 'Vérification du compte', data: { screen: 'login.verification-compte' } },
    { path: '**', component: NotFoundComponent },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
    exports: [RouterModule]
})
export class AppRoutingModule { }
